import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form as FormMik } from 'formik';
import { Button, Form, Image } from 'react-bootstrap';
import * as Yup from 'yup';
import { FiCheckCircle, FiPlus, FiXCircle } from 'react-icons/fi';
import List from '../components/List/List';
import ListColor from '../components/ListColor';
import { useToast } from '../hooks/toast';

import api from '../services/apiClient';

import IGroupUser from '../interfaces/IGroupUser';
import IGroup from '../interfaces/IGroup';

import PencilIcon from '../assets/icon/pencil.svg';
import DeleteIcon from '../assets/icon/delete.svg';
import ModalMessage from '../components/ModalMessage';
import ModalYesNo from '../components/ModalYesNo';

interface DataGroup extends IGroup {
  qtdeUsuario: number;
}

const stickyHeaderCellStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: '#D9D9D9',
};

const extractApiMessage = (data: any): string => {
  if (typeof data === 'string') return data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.Message === 'string') return data.Message;
  return '';
};

const shouldOfferDisable = (message: string): boolean => {
  if (!message) return false;

  const normalized = message
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  return (
    normalized.includes('existem mensagens vinculadas') &&
    normalized.includes('deseja desativa')
  );
};

const Group: React.FC<any> = () => {
  const [groups, setGroups] = useState<DataGroup[]>([]);
  const [newGroup, setNewGroup] = useState(false);
  const [editGroup, setEditGroup] = useState<DataGroup>({} as DataGroup);
  const [groupToDelete, setGroupToDelete] = useState<DataGroup>(
    {} as DataGroup
  );
  const [showMsgDelete, setShowMsgDelete] = useState(false);
  const [showMsgDisable, setShowMsgDisable] = useState(false);
  const [disableMessage, setDisableMessage] = useState('');
  const [userGroups, setUserGroups] = useState<IGroupUser[]>([]);
  const [colorGroup, setColorGroup] = useState('#616161');
  const [nameGroup, setNameGroup] = useState('');

  const colors = [
    '#009F23',
    '#008B',
    '#DE0000',
    '#1300EF',
    '#FFC107',
    '#EA00EF',
    '#D9D9D9',
    '#D9D9D9',
    '#D9D9D9',
  ];

  const inputNomeGrupo = useRef<HTMLInputElement>(null);

  const { addToast } = useToast();

  const handleUpdateUserGroup = (
    seqUsuario: number,
    membroGrupo: boolean
  ): void => {
    setUserGroups((records) => [
      ...records.map((record) => {
        if (record.seqUsuario === seqUsuario) {
          return {
            ...record,
            membroGrupo: membroGrupo ? 'S' : 'N',
          };
        }
        return record;
      }),
    ]);
  };

  const initialValues: IGroup = {
    nomeGrupo: '',
    ativo: true,
    cor: '',
  };

  const validationSchema = Yup.object().shape({
    nomeGrupo: Yup.string()
      .required('O nome do grupo é obrigatório')
      .max(25, 'O nome do grupo deve ter no máximo 25 caracteres'),
    ativo: Yup.string().required('O status do grupo é obrigatório'),
  });

  useEffect(() => {
    if (editGroup.nomeGrupo === undefined) return;
    setNameGroup(editGroup.nomeGrupo);
  }, [editGroup]);

  const loadGroups = (): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('/grupo', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const group: DataGroup[] = [] as DataGroup[];

        response.data.data.forEach((item: any) => {
          group.push({
            seqGrupo: item.seqGrupo,
            nomeGrupo: item.nomeGrupo,
            ativo: item.ativo === 'S',
            qtdeUsuario: item.qtdeUsuario,
            cor: item.cor,
          });
        });

        setGroups(group);
      });
  };

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('@LiveChat:token');
    if (editGroup.seqGrupo === undefined) return;

    api
      .get(`GrupoUsuario/${editGroup.seqGrupo}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const userGroupDb: IGroupUser[] = [];
        response.data.data.forEach((item: any) => {
          userGroupDb.push({
            id: item.id,
            seqUsuario: item.seqUsuario,
            user: item.user,
            nomeUsuario: item.nomeUsuario,
            seqGrupo: item.seqGrupo,
            membroGrupo: item.membroGrupo,
          });
        });

        setUserGroups(userGroupDb);
      });
  }, [editGroup]);

  useEffect(() => {
    if (newGroup) inputNomeGrupo.current?.focus();
  }, [newGroup]);

  const updateUserGroup = async (dataUser: any): Promise<void> => {
    const token = localStorage.getItem('@LiveChat:token');

    let requestValue: any = null;
    if (userGroups.some((grp) => grp.seqGrupo === 0)) {
      const updatedGroups = userGroups.map((grp) => ({
        ...grp,
        id: grp.seqUsuario,
        seqGrupo: dataUser.seqGrupo,
      }));
      setUserGroups(updatedGroups);
      requestValue = updatedGroups;
    } else {
      requestValue = userGroups;
    }

    const updateUser = await api.post('/GrupoUsuario/UserGroup', requestValue, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (updateUser.data.success) {
      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Usuários do grupo atualizados com sucesso',
      });
    }

    const insertData: DataGroup = {
      seqGrupo: dataUser.seqGrupo,
      nomeGrupo: dataUser.nome,
      ativo: dataUser.ativo === 'S',
      qtdeUsuario: userGroups.filter((item) => item.membroGrupo === 'S').length,
      cor: dataUser.cor,
    };

    setGroups([
      ...groups.filter((grp) => grp.seqGrupo !== dataUser.seqGrupo),
      insertData,
    ]);
  };

  const loadAllUsers = (): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('GrupoUsuario/AllUser', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const userGroupDb: IGroupUser[] = [];
        response.data.data.forEach((item: any) => {
          userGroupDb.push({
            id: item.id,
            seqUsuario: item.seqUsuario,
            user: item.user,
            nomeUsuario: item.nomeUsuario,
            seqGrupo: 0,
            membroGrupo: 'N',
          });
        });

        setUserGroups(userGroupDb);
      });
  };

  const handleSubmit = async (values: IGroup): Promise<void> => {
    const token = localStorage.getItem('@LiveChat:token');

    try {
      if (newGroup) {
        const dataRequest = {
          nome: values.nomeGrupo,
          ativo: values.ativo ? 'S' : 'N',
          cor: colorGroup,
        };

        const response = await api.post('/grupo', dataRequest, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.request.status === 400) {
          addToast({
            type: 'warning',
            title: 'Erro ao cadastrar grupo',
            description: response.request.responseText,
          });

          return;
        }

        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Grupo cadastrado com sucesso',
        });

        if (response.data.success) {
          updateUserGroup(response.data.data);
        }
      } else {
        const dataRequest = {
          seqGrupo: editGroup.seqGrupo,
          nome: values.nomeGrupo,
          ativo: values.ativo ? 'S' : 'N',
          cor: colorGroup,
        };

        const response = await api.put('/grupo', dataRequest, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        updateUserGroup(response.data.data);
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao cadastrar grupo',
      });
    } finally {
      setNewGroup(false);
      setEditGroup({
        nomeGrupo: '',
        ativo: true,
        qtdeUsuario: 0,
        cor: '',
      });
      loadAllUsers();
      setColorGroup('#616161');
    }
  };

  const handleDeleteGroup = async (): Promise<void> => {
    const token = localStorage.getItem('@LiveChat:token');

    try {
      const response = await api.delete(`/grupo/${groupToDelete.seqGrupo}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const apiMessage = extractApiMessage(response?.data);

      if (response?.status >= 400 && shouldOfferDisable(apiMessage)) {
        setDisableMessage(apiMessage);
        setShowMsgDisable(true);
        return;
      }

      if (response.data.success) {
        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Grupo excluído com sucesso.',
        });
        loadGroups();
        setEditGroup({} as DataGroup);
        setNewGroup(false);
      } else {
        if (shouldOfferDisable(apiMessage)) {
          setDisableMessage(apiMessage);
          setShowMsgDisable(true);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro',
          description:
            apiMessage || response.data.message || response.data.Message,
        });
      }
    } catch (err: any) {
      const apiMessage = extractApiMessage(err?.response?.data);

      if (shouldOfferDisable(apiMessage)) {
        setDisableMessage(apiMessage);
        setShowMsgDisable(true);
      } else {
        addToast({
          type: 'error',
          title: 'Erro',
          description: apiMessage || err?.message,
        });
      }
    } finally {
      setShowMsgDelete(false);
    }
  };

  const handleDisableGroup = async (): Promise<void> => {
    const token = localStorage.getItem('@LiveChat:token');

    try {
      const dataRequest = {
        seqGrupo: groupToDelete.seqGrupo,
        nome: groupToDelete.nomeGrupo,
        ativo: 'N',
        cor: groupToDelete.cor,
      };

      const response = await api.put('/grupo', dataRequest, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Grupo desativado com sucesso.',
        });
        loadGroups();
      } else {
        addToast({
          type: 'error',
          title: 'Erro',
          description: response.data.message || response.data.Message,
        });
      }
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: err?.message,
      });
    } finally {
      setShowMsgDisable(false);
    }
  };

  return (
    <div>
      <ModalMessage
        title="Excluir grupo"
        message="Isso irá excluir o grupo selecionado. Gostaria de excluí-lo?"
        handleConfirme={handleDeleteGroup}
        showMessage={showMsgDelete}
        setShowMessage={setShowMsgDelete}
      />

      <ModalYesNo
        title="Atenção"
        message={disableMessage}
        show={showMsgDisable}
        onYes={handleDisableGroup}
        onNo={() => setShowMsgDisable(false)}
      />

      <div className="d-flex">
        <section className="col-6">
          <div
            className="card position-static ms-2"
            style={{
              borderRadius: '0.68rem',
              height: 'Calc(100vh - 16rem)',
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div
              className="table-responsive overflow-hidden h-100"
              style={{
                borderRadius: '0.68rem',
                borderColor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between m-0 pe-2 ps-2 text-white"
                style={{
                  fontWeight: '600',
                  fontSize: '1.7rem',
                  backgroundColor: '#1A1A1A',
                }}
              >
                <span className="pb-1 pt-1 d-flex align-items-center justify-content-start">
                  Grupos
                </span>
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditGroup({
                      nomeGrupo: '',
                      ativo: true,
                      qtdeUsuario: 0,
                      cor: '',
                    });
                    loadAllUsers();
                    setColorGroup('#616161');
                    setNewGroup(true);
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FiPlus style={{ height: '1.38rem', width: '1.38rem' }} />
                    <span className="ms-1">adicionar</span>
                  </div>
                </Button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <table className="table table-nowrap mb-0 table-striped-custom">
                  <thead
                    className="small fw-light text-dark"
                    style={{
                      backgroundColor: '#D9D9D9',
                      borderRadius: '0.68rem!important',
                      fontSize: '1.25rem',
                    }}
                  >
                    <tr style={{ color: '#000' }}>
                      <th className="col ms-3" style={stickyHeaderCellStyle}>
                        Nome do Grupo
                      </th>
                      <th className="col-1" style={stickyHeaderCellStyle}>
                        Ativo
                      </th>
                      <th className="col-2" style={stickyHeaderCellStyle}>
                        {' '}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups
                      .sort((a, b) => a.nomeGrupo.localeCompare(b.nomeGrupo))
                      .map((group) => (
                        <tr
                          key={group.seqGrupo}
                          style={{
                            color: '#000',
                            fontSize: '1.25rem',
                            fontWeight: '600',
                          }}
                        >
                          <td className="col ms-1">
                            <span>{group.nomeGrupo}</span>
                          </td>
                          <td className="col-1">
                            <div className="d-flex align-items-center justify-content-center pt-2">
                              {group.ativo ? (
                                <FiCheckCircle
                                  style={{
                                    color: 'green',
                                    height: '1.8rem',
                                    width: '1.8rem',
                                  }}
                                />
                              ) : (
                                <FiXCircle
                                  style={{
                                    color: 'red',
                                    height: '1.8rem',
                                    width: '1.8rem',
                                  }}
                                />
                              )}
                            </div>
                          </td>
                          <td className="col-2">
                            <div className="d-flex justify-content-end me-2">
                              <Button
                                variant="primary m-0 p-1"
                                onClick={() => {
                                  setEditGroup(group);
                                  setColorGroup(group.cor);
                                  setNewGroup(false);
                                  inputNomeGrupo.current?.focus();
                                }}
                              >
                                <Image
                                  src={PencilIcon}
                                  alt="Editar"
                                  style={{ height: '1.4rem', width: '1.5rem' }}
                                />
                              </Button>
                              <Button variant="secondary" className="ms-2 p-1">
                                <Image
                                  src={DeleteIcon}
                                  alt="Deletar"
                                  style={{ height: '1.4rem', width: '1.5rem' }}
                                  onClick={() => {
                                    setGroupToDelete(group);
                                    setShowMsgDelete(true);
                                  }}
                                />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
        <section className="col-6">
          <div
            className="card position-static ms-2"
            style={{
              borderRadius: '0.68rem',
              height: 'Calc(100vh - 16rem)',
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
              opacity: `${!editGroup.seqGrupo && !newGroup ? '0.5' : '1'}`,
            }}
          >
            <div
              className="table-responsive overflow-hidden ms-2 mt-1"
              style={{
                borderRadius: '0.68rem',
                borderColor: 'transparent',
                display: 'flex flex-row',
                pointerEvents: `${
                  !editGroup.seqGrupo && !newGroup ? 'none' : 'auto'
                }`,
              }}
            >
              <div className="mt-1 me-2 ms-1">
                <Formik
                  initialValues={editGroup || initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize
                  onSubmit={async (values, { resetForm }) => {
                    handleSubmit(values);
                    resetForm();
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                    resetForm,
                  }) => (
                    <FormMik>
                      <Form.Group controlId="nomeGrupoControl">
                        <Form.Label
                          style={{ fontSize: '1.25rem', fontWeight: '600' }}
                        >
                          Nome do Grupo
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Nome do Grupo"
                          name="nomeGrupo"
                          onChange={(e) => {
                            setNameGroup(e.target.value);
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          ref={inputNomeGrupo}
                          value={values.nomeGrupo}
                          className={`inputBox
                            ${
                              touched.nomeGrupo && errors.nomeGrupo
                                ? 'is-invalid'
                                : ''
                            }`}
                        />
                        {touched.nomeGrupo && errors.nomeGrupo && (
                          <div className="invalid-feedback">
                            {errors.nomeGrupo}
                          </div>
                        )}
                      </Form.Group>
                      <Form.Check
                        type="checkbox"
                        name="ativo"
                        label="Ativo"
                        style={{ fontSize: '1.25rem', fontWeight: '600' }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        checked={values.ativo}
                        className="mt-2 input-CheckBox d-flex align-items-center justify-content-start"
                      />
                      <div
                        className="table-responsive overflow-hidden mt-2"
                        style={{
                          borderRadius: '0.68rem',
                          borderColor: 'transparent',
                        }}
                      >
                        <div
                          className="w-100 text-white p-2 "
                          style={{ backgroundColor: '#1A1A1A' }}
                        >
                          <span
                            style={{ fontSize: '1.25rem', fontWeight: '600' }}
                          >
                            Usuários vinculados ao grupo
                          </span>
                        </div>
                        <div
                          className="rounded w-100 border-0 overflow-auto"
                          style={{
                            height: '25rem',
                            pointerEvents: `${
                              !editGroup.seqGrupo && !newGroup ? 'none' : 'auto'
                            }`,
                          }}
                        >
                          <List
                            data={userGroups}
                            handleUpdateRegister={handleUpdateUserGroup}
                            idField="seqUsuario"
                            nameField="nomeUsuario"
                            checkedField="membroGrupo"
                          />
                        </div>
                      </div>
                      <div className="d-flex justify-content-center ms-1 mt-2 p-2">
                        <Button
                          type="button"
                          className="bg-white border-white"
                          style={{
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            color: '#616161',
                            boxShadow: '0px 3px 4px -1px rgba(0, 0, 0, 0.25)',
                            borderRadius: '0.6875rem',
                            height: '2.8rem',
                          }}
                          onClick={() => {
                            setEditGroup({
                              nomeGrupo: '',
                              ativo: true,
                              qtdeUsuario: 0,
                              cor: '',
                            });
                            loadAllUsers();
                            setColorGroup('#616161');
                            setNewGroup(true);
                            resetForm();
                          }}
                        >
                          limpar dados
                        </Button>
                        <Button
                          variant="primary"
                          type="submit"
                          className="ms-4"
                          disabled={isSubmitting}
                          style={{
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            height: '2.8rem',
                          }}
                        >
                          confirmar
                        </Button>
                      </div>
                    </FormMik>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Group;
